import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { shallowEqual, useSelector, useDispatch } from "react-redux";

import { CardProps, focus, InputTypes, StateProps } from "../../store";
import CardFooter from "../CardFooter";
import CardHeader from "../CardHeader";
import ItemTypeSection from "../ItemTypeSection";
import TextFieldSection from "../TextFieldSection";
import * as S from "./styles";
import AddTitleImage from "../AddTitleImage";
import InputSubText from "../InputSubText";
import FileIcon from "../../assets/file.png";
import SubText from "../SubText";
import LengthMinInput from "../LengthMinInput";

export interface extendedCardProps extends CardProps {
  isTitle: boolean;
  idx: number;
}

const Card = ({ isTitle, id, idx }: extendedCardProps) => {
  const dispatch = useDispatch();

  const isFocused = useSelector((state: StateProps) => {
    const currentCard = state.cards.find((card) => card.id === id) as CardProps;
    return currentCard.isFocused;
  }, shallowEqual);

  const { inputType } = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
    shallowEqual,
  );

  const title = useSelector((state: StateProps) => {
    const currentCard = state.cards.find((card) => card.id === id) as CardProps;
    return currentCard.cardTitle;
  }, shallowEqual);

  const card = useSelector(
    (state: StateProps) => state.cards.find((card) => card.id === id) as CardProps,
  );

  const isTitleSection =
    card.inputType === InputTypes.SECTION && card.id.split("-")[1] === "titleSection";

  const isFileCard = card.inputType === InputTypes.FILE;

  const setIsFocused = () => {
    if (!isFocused) dispatch(focus({ id }));
  };

  const isSection =
    card.inputType === InputTypes.SECTION || card.inputType === InputTypes.ADDABLE_SECTION;

  return (
    <div>
      {isTitleSection ? (
        <AddTitleImage id={id} image={title} />
      ) : (
        <Draggable draggableId={id} index={idx} key={id}>
          {(provided) => (
            <S.Container
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              isFocused={isFocused}
              isSection={isSection}
              onClick={setIsFocused}
              id={id}
            >
              {isSection ? (
                <S.Section
                  isFocused={isFocused}
                  onClick={() => {
                    setIsFocused();
                  }}
                >
                  <S.CardDndHandle {...provided.dragHandleProps}>
                    <S.Dots />
                  </S.CardDndHandle>
                  <S.ClickHighlight isFocused={isFocused} />
                  <CardHeader isTitle={isTitle} id={id} />
                  {isFocused && !isTitle ? <CardFooter id={id} /> : null}
                </S.Section>
              ) : (
                <S.Card
                  isFocused={isFocused}
                  isTitle={isTitle}
                  onClick={() => {
                    setIsFocused();
                  }}
                >
                  {!isTitle ? (
                    <S.CardDndHandle {...provided.dragHandleProps}>
                      <S.Dots />
                    </S.CardDndHandle>
                  ) : null}
                  <S.ClickHighlight isFocused={isFocused} />
                  <CardHeader isTitle={isTitle} id={id} />
                  {isFocused || isTitle ? <InputSubText id={id} /> : null}
                  {!isFocused && !isTitle ? <SubText id={id} /> : null}
                  {isFocused &&
                  (inputType === InputTypes.TEXTAREA || inputType === InputTypes.TEXT) ? (
                    <LengthMinInput id={id} />
                  ) : null}
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {!isTitle && !isFileCard ? (
                    inputType === InputTypes.TEXT ||
                    inputType === InputTypes.TEXTAREA ||
                    inputType === InputTypes.NAME ||
                    inputType === InputTypes.CONTACT ||
                    inputType === InputTypes.SECTION ? (
                      <TextFieldSection id={id} />
                    ) : (
                      <ItemTypeSection id={id} />
                    )
                  ) : null}
                  {isFileCard ? <S.FileTypeSection src={FileIcon} /> : null}
                  {isFocused && !isTitle ? <CardFooter id={id} /> : null}
                  {isTitle ? <S.RequireMark>* 필수로 체크한 항목</S.RequireMark> : null}
                </S.Card>
              )}
            </S.Container>
          )}
        </Draggable>
      )}
    </div>
  );
};

export default React.memo(Card);
